import React, { useState, useCallback, memo } from "react";
import PropTypes from "prop-types";

import { validateColumns } from "../molGrid/libs/Validation/gridValidation";
import { GridColumnType } from "./libs/data/gridColumnType";
import { EditGridColumnType } from "./libs/data/editGridColumnType";

import { renderMaskedColumn } from "./GridColumns/MaskedColumn";
import { renderGridLinkColumn } from "./GridColumns/LinkColumn";
import { renderGridDateColumn } from "./GridColumns/DateColumn";
import { renderGridLabelColumn } from "./GridColumns/LabelColumn";
import { renderGridMoneyColumn } from "./GridColumns/MoneyColumn";
import RenderGridCheckboxColumn from "./GridColumns/CheckBoxColumn";
import RenderGridToggleColumn from "./GridColumns/ToggleColumn";
import RenderDropdownColumn from "./GridColumns/DropdownColumn";
import RenderGridRadioButtonColumn from "./GridColumns/RadioButtonColumn";

// Edit Columns
import EditInputColumn from "./GridColumns/EditColumns/EditInputColumn";
import EditCheckBoxColumn from "./GridColumns/EditColumns/EditCheckBoxColumn";
import EditDropdownColumn from "./GridColumns/EditColumns/EditDropdownColumn";
import EditDatepickerColumn from "./GridColumns/EditColumns/EditDatepickerColumn";

/** Experiment */
import RenderGridAction from "./GridColumns/ActionColumn";
import RenderEditGridAction from "./GridColumns/EditColumns/EditActionColumn";
import { propTypes } from "react-bootstrap/esm/Image";

// Function for rendering the action column
const MolGridDataRows = ({
  dataSource,
  childTableDataSource,
  columns,
  configuration,
  customColumnClass,
  customHeaderClass,
  allowEditGrid,
  editGridSettings,
  selectedRowIndex,
  onColumnDataChange,
  onRowDataUpdateSaving,
  onActionChange,
  onToggleRow,
  collapsedRows,
  onRowDataDelete,
  onHandleRowSelect,
  onBeforeActionRender,
  rowDoubleClick,
  allowEditDoubleClick
}) => {

  const [editRowData, setEditRowData] = useState([]);
  const [editRowIndex, setEditRowIndex] = useState([]);
  const [errors, setErrors] = useState([]);

  const toggleRow = useCallback((rowId) => {
    onToggleRow(rowId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Row Click
  const handleRowClick = (e, rowData, rowIndex) => {
    if (onHandleRowSelect) {
      onHandleRowSelect(rowData, rowIndex);
    } 
  };
 // Handle Row Double Click
 const handleDoubleClick = (e, rowData, rowIndex) => {
  if (rowDoubleClick) {
    if (allowEditGrid) {
      setEditRowIndex(prev =>
        prev.includes(rowIndex)
          ? prev.filter(index => index !== rowIndex)
          : [...prev, rowIndex]                      
      );
    }
    rowDoubleClick(rowData, rowIndex);
  }
}
  /**
   * Handles saving the edited row data.
   * @param {number} rowIndex - The index of the row being saved.
   */
  const handleRowEditSave = useCallback(
    (rowIndex) => {
      if (editRowIndex.includes(rowIndex) || editGridSettings?.defualtEditableView) {
        const updatedRowData = editRowData[rowIndex];
        // if (updatedRowData) {
          const newErrors = validateColumns(columns, updatedRowData);
          if (Object.keys(newErrors).length === 0) {
            if (onRowDataUpdateSaving) {
              onRowDataUpdateSaving(updatedRowData, rowIndex);
            }
            handleCancelEdit(rowIndex,true);
          } else {
            setErrors((prevErrors) => ({ ...prevErrors, [rowIndex]: newErrors }));
          }
        // }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editRowIndex,editRowData, columns, onRowDataUpdateSaving]
  );

  /**
   * Handles updating the row data.
   * @param {number} rowIndex - The index of the row being updated.
   * @param {object} updatedData - The updated data for the row.
   */
  const handleRowDataUpdate = useCallback(
    (rowIndex, newRowData, fieldName) => {

      const updatedRowData = [...editRowData];


      if (newRowData) {
        const newErrors = validateColumns(columns, newRowData);

        if (Object.keys(newErrors).length === 0) {
          setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[rowIndex];
            return updatedErrors;
          });
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, [rowIndex]: newErrors }));
        }

      }


      updatedRowData[rowIndex] = { ...updatedRowData[rowIndex], ...newRowData };
      setEditRowData(updatedRowData);
      const editSettings = columns.find((x) => x.fieldName === fieldName)
      if (onColumnDataChange && editSettings?.editColumn?.allowOnChange) {
        onColumnDataChange(fieldName, newRowData, rowIndex);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editRowData, columns, setEditRowData]
  );


  /**
 * Handles the blur event of the fields and updates row data.
 * @param {number} rowIndex - The index of the row being updated.
 * @param {object} newRowData - The updated data for the row.
 * @param {string} fieldName - The name of the field being updated.
 */
  const handleFieldBlur = useCallback(
    (rowIndex, newRowData, fieldName) => {
      // Clone the current row data to avoid direct mutation
      const updatedRowData = [...editRowData];

      // If there is new data, perform validation
      if (newRowData) {
        const newErrors = validateColumns(columns, newRowData);

        // Update errors based on the validation result
        if (Object.keys(newErrors).length === 0) {
          setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[rowIndex]; // Remove errors for the current row
            return updatedErrors;
          });
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, [rowIndex]: newErrors })); // Add errors for the current row
        }
      }

      // Update the row data in the state
      updatedRowData[rowIndex] = { ...updatedRowData[rowIndex], ...newRowData };
      setEditRowData(updatedRowData);

      // Find the column settings for the current field name
      const editSettings = columns.find((x) => x.fieldName === fieldName);

      // Call onColumnDataChange if allowed and provided
      if (onColumnDataChange && editSettings?.editColumn?.allowOnChange) {
        onColumnDataChange(fieldName, newRowData, rowIndex);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editRowData, columns, setEditRowData]
  );


  /**
   * Cancels the edit mode for a row.
   * @param {number} rowIndex - The index of the row to cancel edit mode for.
   */
  const handleCancelEdit = useCallback(
    (rowIndex) => {
      if (editRowIndex.includes(rowIndex) ) {
        const newEditRowIndex = editRowIndex.filter(
          (index) => index !== rowIndex
        );
        const newErrors = { ...errors };
        delete newErrors[rowIndex];
        setErrors(newErrors);
        setEditRowIndex(newEditRowIndex);
      }
    },
    [editRowIndex, errors, setEditRowIndex]
  );

  /**
   * Initiates the edit mode for a row.
   * @param {number} rowIndex - The index of the row to edit.
   * @param {object} data - The initial data for the row being edited.
   */
  const handleEditRow = useCallback(
    (rowIndex, data) => {
      const editRowIndexes = [...editRowIndex];

      if (!editRowIndexes.includes(rowIndex)) {
        editRowIndexes.push(rowIndex);
      }

      const editedData = [...editRowData];
      editedData[rowIndex] = data;

      setEditRowData(editedData);
      setEditRowIndex(editRowIndexes);
    },
    [editRowIndex, editRowData, setEditRowIndex]
  );

  const renderGridCol = (rowData, col, rowIndex, ...rest) => {
    const isRowEdited = editRowIndex.includes(rowIndex);
    const editedData = editRowData[rowIndex] || null;

    if (
      col.colType !== GridColumnType.ACTION &&
      ((!isRowEdited && !editGridSettings?.defualtEditableView) || !col.allowEditColumn)
    ) {
      return renderNonEditableColumn(rowData, col, rowIndex);
    }

    if ((col.allowEditColumn && isRowEdited) || (col.allowEditColumn && editGridSettings?.defualtEditableView)) {
      return renderEditableColumn(rowData, col, rowIndex, editedData);
    }

    if (col.colType === GridColumnType.ACTION) {
      return renderActionColumn(rowData, col, rowIndex, isRowEdited);
    }

    return rowData[col.fieldName];
  };

  const renderNonEditableColumn = (rowData, col, rowIndex) => {
    switch (col.colType) {
      case GridColumnType.DATE:
        return renderGridDateColumn(rowData, col, rowIndex);
      case GridColumnType.MONEY:
        return renderGridMoneyColumn(rowData, col, rowIndex);
      case GridColumnType.LABLE:
        return renderGridLabelColumn(rowData, col, rowIndex);
      case GridColumnType.LINK:
        return renderGridLinkColumn(rowData, col, rowIndex);
      case GridColumnType.MASKED:
        return renderMaskedColumn(rowData, col, rowIndex);
      case GridColumnType.RADIO:
        return (
          <RenderGridRadioButtonColumn
            rowData={rowData}
            col={col}
            rowIndex={rowIndex}
            onColumnDataChange={onColumnDataChange}
          />
        );
      case GridColumnType.CHECKBOX:
        return (
          <RenderGridCheckboxColumn
            rowData={rowData}
            col={col}
            rowIndex={rowIndex}
            onColumnDataChange={onColumnDataChange}
          />
        );
      case GridColumnType.TOGGLE:
        return (
          <RenderGridToggleColumn
            rowData={rowData}
            col={col}
            rowIndex={rowIndex}
            onColumnDataChange={onColumnDataChange}
          />
        );
      case GridColumnType.DROPDOWN:
        return (
          <RenderDropdownColumn
            rowData={rowData}
            col={col}
            rowIndex={rowIndex}
            onColumnDataChange={onColumnDataChange}
          />
        );
      case GridColumnType.CUSTOM:
        return col.renderCustomCol
          ? col.renderCustomCol(rowData, rowIndex, onColumnDataChange)
          : "";
      default:
        return rowData[col.fieldName];
    }
  };

  const renderEditableColumn = (rowData, col, rowIndex, editRowData) => {
    const commonProps = {
      rowData,
      col,
      editColumn: col.editColumn,
      rowIndex,
      onChange: handleRowDataUpdate,
      onBlur: handleFieldBlur,
      updatedData: editRowData,
      errors,
      onColumnDataChange: onColumnDataChange,
    };

    switch (col.editColumn.editColType) {
      case EditGridColumnType.INPUT:
      case EditGridColumnType.NUMERIC:
        return <EditInputColumn {...commonProps} />;
      case EditGridColumnType.DROPDOWN:
        return <EditDropdownColumn {...commonProps} />;
      case EditGridColumnType.DATEPICKER:
        return <EditDatepickerColumn {...commonProps} />;
      case EditGridColumnType.CHECKBOX:
        return <EditCheckBoxColumn {...commonProps} />;
      default:
        return rowData[col.fieldName];
    }
  };

  const renderActionColumn = (rowData, col, rowIndex, isRowEdited) => {
    if ((allowEditGrid && isRowEdited) || (allowEditGrid && editGridSettings?.defualtEditableView)) {
      return (
        <RenderEditGridAction
          rowData={rowData}
          col={col}
          rowIndex={rowIndex}
          allowEditGrid={allowEditGrid}
          onRowEditSave={handleRowEditSave}
          isRowEditable={isRowEdited || editGridSettings?.defualtEditableView}
          onRowEditCancel={handleCancelEdit}
          editGridSettings={editGridSettings}
          onRowDataDelete={onRowDataDelete}
        />
      )
    } else {
      return (
        <RenderGridAction
          rowData={rowData}
          col={col}
          rowIndex={rowIndex}
          onActionHandler={onActionChange}
          allowEditGrid={allowEditGrid}
          isRowEditable={isRowEdited}
          onEditRow={handleEditRow}
          onBeforeActionRender={onBeforeActionRender}
        />
      )
    }
  };

  const renderChildGridHeader = () =>
    !configuration.childGridSetting.hideChildHeader ? (
      <tr>
        {configuration.ChildTableColumn.map((col) => (
          <th
            key={`childcol_${col.name}`}
            className={customHeaderClass || ""}
            style={{
              width: col.colStyle?.width || "auto",
              textAlign: col.colStyle?.textAlign || "left",
              ...col.colStyle,
            }}
          >
            {col.name}
          </th>
        ))}
      </tr>
    ) : null;

  const renderChildGridRow = (childDataSource, parentRow) => (
    <>
      {childDataSource
        .filter(
          (childRow) =>
            childRow[configuration.childGridSetting.childKeyDataField] ===
            parentRow[configuration.childGridSetting.parentKeyDataField]
        )
        .map((row, rowIndex) => (
          <React.Fragment key={`row_${rowIndex + 1}`}>
            <tr>
              {configuration.ChildTableColumn.map((col) => (
                <td
                  key={col.name}
                  className={customColumnClass || ""}
                  style={
                    col.colStyle?.width ? { width: col.colStyle.width } : null
                  }
                >
                  {renderGridCol(row, col, rowIndex, parentRow)}
                </td>
              ))}
            </tr>
          </React.Fragment>
        ))}
    </>
  );

  const renderChildGrid = (
    configuration,
    parentRow,
    childDataSource,
    rowIndex
  ) =>
    collapsedRows[rowIndex] ? (
      <tr>
        <td className="first-td"></td>
        <td colSpan={configuration.columns.length} className="sub-table">
          <table>
            {renderChildGridHeader()}
            {renderChildGridRow(childDataSource, parentRow)}
          </table>
        </td>
      </tr>
    ) : null;

  return (
    <>
      {dataSource.map((row, rowIndex) => (
        <React.Fragment key={`row_${rowIndex + 1}`}>
          <tr
            key={`row_${rowIndex + 1}`}
            className={`parent-row ${collapsedRows[rowIndex] ? "collapsed" : ""} ${configuration?.isRowSelectable ? 'selectable-row-hover' : ''} ${onHandleRowSelect && configuration?.isRowSelectable && selectedRowIndex === rowIndex ? 'selected-row' : ''}`}
            onClick={(e) => handleRowClick(e, row, rowIndex)}
            onDoubleClick={(e) => handleDoubleClick(e,row, rowIndex)}
          >
            {configuration.hasChildGridTable ? (
              <td className="first-td">
                <button
                  style={{ all: "unset" }}
                  onClick={() => toggleRow(rowIndex)}
                >
                  <span className={`bi bi-chevron-${collapsedRows[rowIndex] ? "down" : "right"}`}
                  ></span>
                </button>
              </td>
            ) : null}
            {columns.map((col, colIndex) => (
              <td
                key={`col_${col.name}_${rowIndex + 1}`}
                className={`whitespace-nowrap ${customColumnClass || ""}`}
                onClick={col.colType === GridColumnType.ACTION && !allowEditDoubleClick ? (e) => e.stopPropagation() : undefined}
                style={{
                  width: col.colStyle?.width || "auto",
                  textAlign: col.colStyle?.textAlign || "center",
                  ...col.colStyle,
                }}
              >
                <div className="input-validation">
                  {renderGridCol(row, col, rowIndex)}
                </div>
              </td>
            ))}
          </tr>
          {configuration.hasChildGridTable &&
            renderChildGrid(configuration, row, childTableDataSource, rowIndex)}
        </React.Fragment>
      ))}
    </>
  );
};

MolGridDataRows.propTypes = {
  dataSource: PropTypes.array.isRequired,
  childTableDataSource: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  configuration: PropTypes.object.isRequired,
  customColumnClass: PropTypes.string,
  customHeaderClass: PropTypes.string,
  allowEditGrid: PropTypes.bool,
  editGridSettings: PropTypes.object,
  selectedRowIndex: PropTypes.number,
  onColumnDataChange: PropTypes.func,
  onRowDataUpdateSaving: PropTypes.func,
  onActionChange: PropTypes.func,
  onRowSelect: PropTypes.func,
  onHandleRowSelect: PropTypes.func,
  onRowDataDelete: PropTypes.func,
  onToggleRow: PropTypes.func,
  collapsedRows: PropTypes.object,
  onBeforeActionRender: PropTypes.func,
  rowDoubleClick: propTypes.func,
  allowEditDoubleClick: propTypes.func
};

export default memo(MolGridDataRows);
